import React, { useCallback, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAction, useMutation, useQuery } from 'convex/react'
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  BookOpenIcon,
  ArrowTopRightOnSquareIcon,
  LinkIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { api } from '../convex_generated/api'
import { useToast } from '../contexts/ToastContext'
import { BOOK_GENRES, GENRE_BADGE_COLORS, type BookGenre } from '../constants/genres'

// ---------------------------------------------------------------------------
// Types (mirroring what the action returns)
// ---------------------------------------------------------------------------
interface GutenbergResult {
  gutenbergId: string
  title: string
  author: string
  subjects: string[]
  languages: string[]
  coverImage: string | null
  hasText: boolean
  textUrl: string | null
  epubUrl: string | null
  genres: string[]
  downloadCount: number
}

// ---------------------------------------------------------------------------
// Simple skeleton card
// ---------------------------------------------------------------------------
const SkeletonCard: React.FC = () => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
    <div className="flex gap-4">
      <div className="w-16 h-24 rounded-lg bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
      <div className="flex-1 space-y-2 py-1">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-28 mt-4" />
      </div>
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// Single result card
// ---------------------------------------------------------------------------
const BookResultCard: React.FC<{
  book: GutenbergResult
  onImport: (book: GutenbergResult) => Promise<void>
  importState: 'idle' | 'loading' | 'done' | 'error'
  alreadyImported?: boolean
  importedBookId?: string
}> = ({ book, onImport, importState, alreadyImported, importedBookId }) => {  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 transition-all ${alreadyImported ? 'opacity-60' : 'hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm'}`}>
      <div className="flex gap-4">
        {/* Cover */}
        <div className="flex-shrink-0 w-16">
          {book.coverImage ? (
            <img
              src={book.coverImage}
              alt={book.title}
              className="w-16 h-24 object-cover rounded-lg shadow"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none'
              }}
            />
          ) : (
            <div className="w-16 h-24 rounded-lg bg-gradient-to-br from-primary-100 to-indigo-100 dark:from-primary-900/30 dark:to-indigo-900/30 flex items-center justify-center">
              <BookOpenIcon className="w-6 h-6 text-primary-400" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm leading-snug mb-0.5 line-clamp-2">
            {book.title}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{book.author}</p>

          {/* Genres */}
          {book.genres.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {book.genres.slice(0, 3).map((g) => (
                <span
                  key={g}
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold capitalize ${
                    genreColors[g] ?? 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {g}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3">
            {/* Import button */}
            {alreadyImported ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 dark:text-gray-500">
                <CheckCircleIcon className="w-4 h-4" />
                Already imported
              </span>
            ) : !book.hasText && !book.epubUrl ? (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                No downloadable version available
              </span>
            ) : importState === 'done' ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                <CheckCircleIcon className="w-4 h-4" />
                Imported!
                {importedBookId && (
                  <Link
                    to={`/branches/${importedBookId}`}
                    className="ml-1 underline underline-offset-2 hover:text-emerald-800 dark:hover:text-emerald-300"
                  >
                    View Book →
                  </Link>
                )}
              </span>
            ) : importState === 'error' ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                <ExclamationCircleIcon className="w-4 h-4" />
                Failed – try again
              </span>
            ) : (
              <button
                onClick={() => onImport(book)}
                disabled={importState === 'loading'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white text-xs font-medium rounded-lg transition-colors"
              >
                {importState === 'loading' ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importing…
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                    Import
                  </>
                )}
              </button>
            )}

            {/* Gutenberg link */}
            <a
              href={`https://www.gutenberg.org/ebooks/${book.gutenbergId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
              Gutenberg
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// GutenbergImportPage
// ---------------------------------------------------------------------------

type Tab = 'search' | 'url'

const GENRES = BOOK_GENRES
type GenreLiteral = BookGenre

const genreColors: Record<string, string> = GENRE_BADGE_COLORS

const LANGUAGES: { code: string; label: string }[] = [
  { code: '', label: 'All languages' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'es', label: 'Spanish' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'nl', label: 'Dutch' },
  { code: 'fi', label: 'Finnish' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' },
]

const ERAS: { value: string; label: string }[] = [
  { value: '', label: 'Any era' },
  { value: 'ancient', label: 'Ancient (before 500)' },
  { value: 'medieval', label: 'Medieval (500–1500)' },
  { value: '1700s', label: '18th Century' },
  { value: '1800s', label: '19th Century' },
  { value: '1900s', label: '20th Century' },
]

const ERA_RANGES: Record<string, [number, number]> = {
  ancient:  [-4000, 499],
  medieval: [500, 1499],
  '1700s':  [1700, 1799],
  '1800s':  [1800, 1899],
  '1900s':  [1900, 1999],
}

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'popular', label: 'Most popular' },
  { value: 'ascending', label: 'Oldest first' },
  { value: 'descending', label: 'Newest first' },
]

const GutenbergImportPage: React.FC = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()

  const searchBooks = useAction(api.gutenberg.searchGutenbergBooks)
  const importBook = useAction(api.gutenberg.importGutenbergBook)
  const importByUrl = useAction(api.gutenberg.importByUrl)
  const importFromUpload = useAction(api.gutenberg.importFromUpload)
  const generateUploadUrl = useMutation(api.books.generateCoverUploadUrl)
  const importedData = useQuery(api.gutenberg.getImportedGutenbergIds) as { ids: string[]; titleAuthorKeys: string[] } | undefined
  const importedIdSet = new Set(importedData?.ids ?? [])
  const importedTitleAuthorSet = new Set(importedData?.titleAuthorKeys ?? [])

  const [activeTab, setActiveTab] = useState<Tab>('search')

  // ── Search tab state ──
  const [query, setQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [results, setResults] = useState<GutenbergResult[] | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(1)
  const [searchError, setSearchError] = useState<string | null>(null)

  // ── Filter state ──
  const [showFilters, setShowFilters] = useState(false)
  const [filterGenre, setFilterGenre] = useState<string>('')
  const [filterLanguage, setFilterLanguage] = useState('')
  const [filterEra, setFilterEra] = useState<string>('')
  const [filterSort, setFilterSort] = useState<string>('popular')

  // Simple in-memory cache: "query|page" → { results, count, hasMore }
  const searchCache = useRef<Record<string, { results: GutenbergResult[]; count: number; hasMore: boolean }>>({})

  // Per-book import state: gutenbergId → 'idle' | 'loading' | 'done' | 'error'
  const [importStates, setImportStates] = useState<Record<string, 'idle' | 'loading' | 'done' | 'error'>>({})
  const [importedBookIds, setImportedBookIds] = useState<Record<string, string>>({})
  const setImportState = (id: string, state: 'idle' | 'loading' | 'done' | 'error') =>
    setImportStates((prev) => ({ ...prev, [id]: state }))

  // ── URL import tab state ──
  const [urlInput, setUrlInput] = useState('')
  const [urlTitle, setUrlTitle] = useState('')
  const [urlAuthor, setUrlAuthor] = useState('')
  const [urlGenres, setUrlGenres] = useState<GenreLiteral[]>(['fiction'])
  const [urlImporting, setUrlImporting] = useState(false)
  const [urlError, setUrlError] = useState<string | null>(null)

  // ── File upload state ──
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const lower = file.name.toLowerCase()
    if (!lower.endsWith('.epub') && !lower.endsWith('.txt')) {
      setUrlError('Only .epub and .txt files are supported')
      return
    }
    setUploadFile(file)
    setUrlInput('')  // clear URL when file is picked
    setUrlError(null)
  }

  const handleFileImport = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!uploadFile || !urlTitle.trim() || !urlAuthor.trim()) return
      setUrlImporting(true)
      setUrlError(null)
      try {
        // Upload file to Convex storage
        const postUrl = await generateUploadUrl({})
        const uploadRes = await fetch(postUrl, {
          method: 'POST',
          headers: { 'Content-Type': uploadFile.type || 'application/octet-stream' },
          body: uploadFile,
        })
        const { storageId } = await uploadRes.json()

        // Import from uploaded file
        const { bookId } = await importFromUpload({
          storageId,
          fileName: uploadFile.name,
          title: urlTitle.trim(),
          author: urlAuthor.trim(),
          genre: urlGenres,
        })
        showToast({ message: `"${urlTitle}" imported!`, type: 'success' })
        navigate(`/branches/${bookId}`)
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Import failed'
        setUrlError(msg)
        showToast({ message: msg, type: 'error' })
      } finally {
        setUrlImporting(false)
      }
    },
    [uploadFile, urlTitle, urlAuthor, urlGenres, generateUploadUrl, importFromUpload, navigate, showToast],
  )

  const handleSearch = useCallback(
    async (e?: React.FormEvent, pageNum = 1, overrideQuery?: string) => {
      e?.preventDefault()
      const q = (overrideQuery ?? query).trim()
      const hasAnyFilter = q || filterGenre || filterEra || filterLanguage !== ''
      if (!hasAnyFilter) return

      const eraYears = ERA_RANGES[filterEra as keyof typeof ERA_RANGES]
      const cacheKey = `${q.toLowerCase()}|${filterGenre}|${filterLanguage}|${filterEra}|${filterSort}|${pageNum}`
      const cached = searchCache.current[cacheKey]
      if (cached) {
        setResults((prev) =>
          pageNum === 1 ? cached.results : [...(prev ?? []), ...cached.results],
        )
        setTotalCount(cached.count)
        setHasMore(cached.hasMore)
        setPage(pageNum)
        setSearchError(null)
        return
      }

      setIsSearching(true)
      setSearchError(null)
      if (pageNum === 1) setResults(null)
      setPage(pageNum)
      try {
        const data = await searchBooks({
          query: q,
          page: pageNum,
          genre: filterGenre || undefined,
          languages: filterLanguage || undefined,
          authorYearStart: eraYears?.[0],
          authorYearEnd: eraYears?.[1],
          sort: filterSort !== 'popular' ? filterSort : undefined,
        })
        const typed = data.results as GutenbergResult[]
        searchCache.current[cacheKey] = { results: typed, count: data.count, hasMore: data.hasMore }
        setResults((prev) =>
          pageNum === 1 ? typed : [...(prev ?? []), ...typed],
        )
        setTotalCount(data.count)
        setHasMore(data.hasMore)
      } catch (err) {
        setSearchError(err instanceof Error ? err.message : 'Search failed')
      } finally {
        setIsSearching(false)
      }
    },
    [query, filterGenre, filterLanguage, filterEra, filterSort, searchBooks],
  )

  const handleImport = useCallback(
    async (book: GutenbergResult) => {
      if (!book.textUrl && !book.epubUrl) return
      setImportState(book.gutenbergId, 'loading')
      try {
        const { bookId } = await importBook({
          gutenbergId: book.gutenbergId,
          title: book.title,
          author: book.author,
          subjects: book.subjects,
          languages: book.languages,
          coverImage: book.coverImage ?? undefined,
          textUrl: book.textUrl ?? undefined,
          epubUrl: book.epubUrl ?? undefined,
        })
        setImportState(book.gutenbergId, 'done')
        setImportedBookIds((prev) => ({ ...prev, [book.gutenbergId]: bookId }))
        showToast({ message: `"${book.title}" imported!`, type: 'success' })
      } catch (err) {
        setImportState(book.gutenbergId, 'error')
        showToast({
          message: err instanceof Error ? err.message : 'Import failed',
          type: 'error',
        })
      }
    },
    [importBook, showToast],
  )

  const handleUrlImport = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!urlInput.trim() || !urlTitle.trim() || !urlAuthor.trim()) return
      setUrlImporting(true)
      setUrlError(null)
      try {
        const { bookId } = await importByUrl({
          sourceUrl: urlInput.trim(),
          title: urlTitle.trim(),
          author: urlAuthor.trim(),
          genre: urlGenres,
        })
        showToast({ message: `"${urlTitle}" imported!`, type: 'success' })
        navigate(`/branches/${bookId}`)
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Import failed'
        setUrlError(msg)
        showToast({ message: msg, type: 'error' })
      } finally {
        setUrlImporting(false)
      }
    },
    [importByUrl, urlInput, urlTitle, urlAuthor, urlGenres, navigate, showToast],
  )

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero header */}
      <div className="bg-gradient-to-br from-primary-700 via-primary-600 to-indigo-600 text-white">
        <div className="max-w-3xl mx-auto px-4 py-10 sm:px-6">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Import a Book</h1>
          <p className="text-primary-100 text-base mb-6">
            Search 70,000+ free Gutenberg classics, paste a <code className="bg-white/20 rounded px-1">.txt</code> / <code className="bg-white/20 rounded px-1">.epub</code> URL, or upload a file from your device.
          </p>

          {/* Tabs */}
          <div className="flex gap-1 bg-white/10 rounded-xl p-1 w-fit mb-6">
            <button
              onClick={() => setActiveTab('search')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === 'search'
                  ? 'bg-white text-primary-700 shadow'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              <MagnifyingGlassIcon className="w-4 h-4" />
              Search Gutenberg
            </button>
            <button
              onClick={() => setActiveTab('url')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === 'url'
                  ? 'bg-white text-primary-700 shadow'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              <LinkIcon className="w-4 h-4" />
              Import from URL
            </button>
          </div>

          {/* Search bar (search tab only) */}
          {activeTab === 'search' && (
            <div className="space-y-3">
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder='Search by title or author, e.g. "Sherlock Holmes"…'
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 border-0 shadow focus:outline-none focus:ring-2 focus:ring-primary-300 text-sm"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSearching || (!query.trim() && !filterGenre && !filterEra && filterLanguage === '')}
                  className="px-5 py-3 bg-white text-primary-700 font-semibold rounded-xl hover:bg-primary-50 disabled:opacity-60 transition-colors text-sm shadow"
                >
                  {isSearching ? 'Searching…' : 'Search'}
                </button>
              </form>

              {/* Filter toggle */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilters((v) => !v)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    showFilters || filterGenre || filterLanguage !== '' || filterEra || filterSort !== 'popular'
                      ? 'bg-white text-primary-700 shadow'
                      : 'bg-white/15 text-white/80 hover:text-white hover:bg-white/25'
                  }`}
                >
                  <AdjustmentsHorizontalIcon className="w-3.5 h-3.5" />
                  Filters
                  {(filterGenre || filterLanguage !== '' || filterEra || filterSort !== 'popular') && (
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  )}
                </button>

                {/* Active filter pills */}
                {filterGenre && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-white/20 text-white capitalize">
                    {filterGenre}
                    <button onClick={() => setFilterGenre('')} className="hover:text-red-200"><XMarkIcon className="w-3 h-3" /></button>
                  </span>
                )}
                {filterLanguage !== '' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-white/20 text-white">
                    {LANGUAGES.find((l) => l.code === filterLanguage)?.label ?? filterLanguage}
                    <button onClick={() => setFilterLanguage('')} className="hover:text-red-200"><XMarkIcon className="w-3 h-3" /></button>
                  </span>
                )}
                {filterEra && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-white/20 text-white">
                    {ERAS.find((e) => e.value === filterEra)?.label ?? filterEra}
                    <button onClick={() => setFilterEra('')} className="hover:text-red-200"><XMarkIcon className="w-3 h-3" /></button>
                  </span>
                )}
              </div>

              {/* Filter panel */}
              {showFilters && (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 space-y-4 border border-white/20">
                  {/* Genre */}
                  <div>
                    <label className="block text-xs font-semibold text-white/70 mb-2 uppercase tracking-wide">Genre / Topic</label>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        onClick={() => setFilterGenre('')}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                          !filterGenre ? 'bg-white text-primary-700 shadow' : 'bg-white/15 text-white/80 hover:bg-white/25'
                        }`}
                      >
                        All
                      </button>
                      {GENRES.map((g) => (
                        <button
                          key={g}
                          onClick={() => setFilterGenre(filterGenre === g ? '' : g)}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${
                            filterGenre === g ? 'bg-white text-primary-700 shadow' : 'bg-white/15 text-white/80 hover:bg-white/25'
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Language */}
                    <div>
                      <label className="block text-xs font-semibold text-white/70 mb-1.5 uppercase tracking-wide">Language</label>
                      <select
                        value={filterLanguage}
                        onChange={(e) => setFilterLanguage(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-white text-gray-900 text-sm border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
                      >
                        {LANGUAGES.map((l) => (
                          <option key={l.code} value={l.code}>{l.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Author era */}
                    <div>
                      <label className="block text-xs font-semibold text-white/70 mb-1.5 uppercase tracking-wide">Author Era</label>
                      <select
                        value={filterEra}
                        onChange={(e) => setFilterEra(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-white text-gray-900 text-sm border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
                      >
                        {ERAS.map((era) => (
                          <option key={era.value} value={era.value}>{era.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Sort */}
                    <div>
                      <label className="block text-xs font-semibold text-white/70 mb-1.5 uppercase tracking-wide">Sort by</label>
                      <select
                        value={filterSort}
                        onChange={(e) => setFilterSort(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-white text-gray-900 text-sm border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
                      >
                        {SORT_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8 sm:px-6">

        {/* ── URL Import tab ── */}
        {activeTab === 'url' && (
          <div className="space-y-6">
            <div className="p-5 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <h3 className="font-semibold text-amber-800 dark:text-amber-300 text-sm mb-2">
                Paste a URL or upload a <code>.txt</code> / <code>.epub</code> file
              </h3>
              <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-1 list-disc list-inside">
                <li><strong>Project Gutenberg (US)</strong> — gutenberg.org — 70,000+ books</li>
                <li><strong>Gutenberg Australia</strong> — gutenberg.net.au — includes post-1927 works</li>
                <li><strong>Gutenberg Canada</strong> — gutenberg.ca</li>
                <li><strong>Standard Ebooks</strong> — standardebooks.org — beautifully formatted EPUBs</li>
                <li><strong>Open Library / Internet Archive</strong> — archive.org</li>
                <li>Any direct link to a <code>.txt</code> or <code>.epub</code> file, or upload from your device</li>
              </ul>
            </div>

            {/* Copyright disclaimer */}
            <div className="p-4 bg-red-50 dark:bg-red-950/30 rounded-xl border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300 space-y-1.5">
              <p className="font-semibold text-red-800 dark:text-red-200 flex items-start gap-1.5">
                <span className="mt-px shrink-0">⚠️</span>
                Copyright &amp; Content Policy
              </p>
              <p>
                You may only import books that are in the <strong>public domain</strong>, released under a
                free/open licence, or for which you personally hold or have licensed the copyright.
              </p>
              <p>
                Books found to be in violation of copyright law will be <strong>removed immediately</strong>.
                The account responsible may be <strong>suspended or permanently banned</strong>, and any
                proceeds earned from that book on StoryBranch will be <strong>returned to the rightful
                copyright holder</strong>.
              </p>
              <p className="text-red-600 dark:text-red-400">
                By importing a book you confirm you have the right to do so.
              </p>
            </div>

            <form onSubmit={uploadFile ? handleFileImport : handleUrlImport} className="space-y-4">
              {/* Source: URL or File upload */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Source — paste a URL <em>or</em> upload a file <span className="text-red-500">*</span>
                </label>

                {/* URL input */}
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => { setUrlInput(e.target.value); setUploadFile(null); setUrlError(null) }}
                  disabled={!!uploadFile}
                  placeholder="https://example.com/book.epub  or  https://example.com/book.txt"
                  className={`input w-full font-mono text-sm ${uploadFile ? 'opacity-40' : ''}`}
                />

                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                  or
                  <span className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                </div>

                {/* File upload */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".epub,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {uploadFile ? (
                  <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                    <ArrowUpTrayIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-primary-700 dark:text-primary-300 truncate flex-1">{uploadFile.name}</span>
                    <span className="text-xs text-primary-500">{(uploadFile.size / 1024).toFixed(0)} KB</span>
                    <button
                      type="button"
                      onClick={() => { setUploadFile(null); if (fileInputRef.current) fileInputRef.current.value = '' }}
                      className="text-primary-400 hover:text-red-500 transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-400 transition-colors"
                  >
                    <ArrowUpTrayIcon className="w-4 h-4" />
                    Upload <code>.epub</code> or <code>.txt</code> file
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Book title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={urlTitle}
                    onChange={(e) => setUrlTitle(e.target.value)}
                    required
                    placeholder="e.g. On the Beach"
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Author <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={urlAuthor}
                    onChange={(e) => setUrlAuthor(e.target.value)}
                    required
                    placeholder="e.g. Nevil Shute"
                    className="input w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Genre (select at least one)
                </label>
                <div className="flex flex-wrap gap-2">
                  {GENRES.map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() =>
                        setUrlGenres((prev) =>
                          prev.includes(g)
                            ? prev.length > 1 ? prev.filter((x) => x !== g) : prev
                            : [...prev, g],
                        )
                      }
                      className={`px-3 py-1 rounded-full text-xs font-semibold capitalize border-2 transition-colors ${
                        urlGenres.includes(g)
                          ? `${genreColors[g]} border-current`
                          : 'border-transparent bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              {urlError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
                  <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
                  {urlError}
                </div>
              )}

              <button
                type="submit"
                disabled={urlImporting || (!urlInput.trim() && !uploadFile) || !urlTitle.trim() || !urlAuthor.trim()}
                className="btn btn-primary w-full sm:w-auto flex items-center gap-2"
              >
                {urlImporting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importing… (this may take a minute)
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="w-4 h-4" />
                    Import book
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* ── Search tab ── */}
        {activeTab === 'search' && (<>
        {/* Quick suggestions */}
        {results === null && !isSearching && (
          <div>
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">
              Popular searches
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                'Frankenstein',
                'Pride and Prejudice',
                'Sherlock Holmes',
                'Dracula',
                'Moby Dick',
                'The Adventures of Tom Sawyer',
                'War and Peace',
                'The Time Machine',
                'Alice in Wonderland',
                'The Count of Monte Cristo',
              ].map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setQuery(s)
                    handleSearch(undefined, 1, s)
                  }}
                  className="px-3 py-1.5 bg-white dark:bg-gray-800 rounded-full text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="mt-10 p-5 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <h3 className="font-semibold text-amber-800 dark:text-amber-300 text-sm mb-1">
                How importing works
              </h3>
              <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-1 list-disc list-inside">
                <li>The full text is imported as a root branch</li>
                <li>The full text is fetched from Project Gutenberg's servers</li>
                <li>Your community can branch off into any new direction</li>
                <li>Duplicate imports of the same book are prevented automatically</li>
              </ul>
            </div>
          </div>
        )}

        {/* Loading skeletons */}
        {isSearching && results === null && (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {/* Error */}
        {searchError && (
          <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
            <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
            {searchError}
          </div>
        )}

        {/* Results list */}
        {results !== null && (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {totalCount.toLocaleString()} matching books &mdash; showing {results.length}
              </p>
            </div>

            {results.length === 0 ? (
              <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                <BookOpenIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No results for "{query}"</p>
                <p className="text-sm mt-1">Try a different title or author name.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {results.map((book) => (
                  <BookResultCard
                    key={book.gutenbergId}
                    book={book}
                    onImport={handleImport}
                    importState={importStates[book.gutenbergId] ?? 'idle'}
                    alreadyImported={importedIdSet.has(book.gutenbergId) || importedTitleAuthorSet.has(`${book.title.toLowerCase()}|||${book.author.toLowerCase()}`)}
                    importedBookId={importedBookIds[book.gutenbergId]}
                  />
                ))}
              </div>
            )}

            {/* Load more */}
            {hasMore && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => handleSearch(undefined, page + 1)}
                  disabled={isSearching}
                  className="px-6 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 transition-colors"
                >
                  {isSearching ? 'Loading…' : 'Load more results'}
                </button>
              </div>
            )}
          </>
        )}
      </>
      )}
      </div>
    </div>
  )
}

export default GutenbergImportPage
